import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, Input, Button, Select } from '../../components/ui';
import './AddOrganizationScreen.css';
import { addOrganization, getOrganization, updateOrganization } from '../../controllers/organizations';
import { useToast } from '../../contexts/ToastContext';

const AddOrganizationScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  
  // Determine the mode based on URL parameters and location state
  const mode = location.state?.mode || (id ? 'edit' : 'create');
  const finalMode = mode;
  
  const [organizationName, setOrganizationName] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Debug organizationType changes
  useEffect(() => {
    console.log('organizationType state changed to:', organizationType);
  }, [organizationType]);

  const organizationTypeOptions = [
    { value: 'industry_partner', label: 'Industry Partner' },
    { value: 'site', label: 'Site' }
  ];

  // Load existing organization data for edit mode
  useEffect(() => {
    const loadOrganizationData = async () => {
      if (finalMode === 'edit') {
        if (!id) {
          console.error('No organization ID provided for edit mode');
          return;
        }

        try {
          setLoading(true);
          console.log('Loading organization data for ID:', id);
          const response = await getOrganization({ organization_id: id });
          console.log('Full API response:', response);

          // Log the full response structure for debugging
          console.log('Full response object:', response);
          console.log('Response data:', response.data);
          console.log('Response data keys:', Object.keys(response.data || {}));

          // Check different possible response structures
          let organization = null;
          if (response.data && response.data.success && response.data.data) {
            organization = response.data.data;
            console.log('Found organization in response.data.data');
          } else if (response.data && response.data.organization) {
            organization = response.data.organization;
            console.log('Found organization in response.data.organization');
          } else if (response.organization) {
            organization = response.organization;
            console.log('Found organization in response.organization');
          } else if (response.data) {
            organization = response.data;
            console.log('Using response.data as organization');
          }

          console.log('Extracted organization:', organization);

          if (organization && organization.name) {
            // Populate form fields with existing data
            console.log('Setting organization name to:', organization.name);
            console.log('Setting organization type to:', organization.type);
            setOrganizationName(organization.name || '');
            setOrganizationType(organization.type || '');
            
            console.log('Organization data loaded successfully:', organization);
          } else {
            console.error('Failed to load organization data. Response structure:', response);
            console.error('Expected organization object with name property, got:', organization);
            console.error('Organization keys:', organization ? Object.keys(organization) : 'null');
            showError(`Failed to load organization data. Check console for details.`);
            navigate('/home', { state: { activeSection: 'Dashboard Access' } });
          }
        } catch (error) {
          console.error('Error loading organization data:', error);
          console.error('Error details:', error.response || error.message);
          showError(`Failed to load organization data. Error: ${error.message}`);
          navigate('/home', { state: { activeSection: 'Dashboard Access' } });
        } finally {
          setLoading(false);
        }
      }
    };

    loadOrganizationData();
  }, [id, finalMode, navigate]);

  const handleBackClick = () => {
    navigate(-1); // Navigate back to previous page
  };

  const handleSubmit = async () => {
    if (!organizationName.trim()) {
      showError('Please enter an organization name');
      return;
    }

    if (!organizationType) {
      showError('Please select an organization type');
      return;
    }

    try {
      setSubmitting(true);
      console.log(`${finalMode} organization...`);
      
      const organizationData = {
        name: organizationName.trim(),
        type: organizationType
      };
      
      console.log('Form data being submitted:');
      console.log('organizationName:', organizationName);
      console.log('organizationType:', organizationType);
      console.log('organizationData:', organizationData);

      // Add organization ID for update operations
      if (finalMode === 'edit' && id) {
        organizationData.organization_id = id;
      }

      let response;
      if (finalMode === 'edit') {
        response = await updateOrganization(organizationData);
      } else {
        response = await addOrganization(organizationData);
      }
      
      console.log('API response:', response);
      
      // Check for success in different possible response structures
      const isSuccess = (response && response.success) || 
                       (response && response.data && response.data.success) ||
                       (response && response.status === 200);
      
      if (isSuccess) {
        const action = finalMode === 'edit' ? 'updated' : 'created';
        console.log(`Organization ${action} successfully:`, response);
        showSuccess(`Organization ${action} successfully!`);
        
        // Navigate to Dashboard Access page
        navigate('/home', { 
          state: { activeSection: 'Dashboard Access' } 
        });
      } else {
        const action = finalMode === 'edit' ? 'update' : 'create';
        console.error(`Failed to ${action} organization. Response:`, response);
        showError(`Failed to ${action} organization. Please try again.`);
      }
    } catch (error) {
      console.error(`Error ${finalMode === 'edit' ? 'updating' : 'creating'} organization:`, error);
      const action = finalMode === 'edit' ? 'update' : 'create';
      showError(`Failed to ${action} organization. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get the appropriate title
  const getTitle = () => {
    switch (finalMode) {
      case 'edit': return 'Edit Organization';
      default: return 'Add New Organization';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="create-incubation-screen">
        <button className="back-button-screen" onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
        <div className="add-organization-container">
          <Card style={{ width: '500px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>Loading organization data...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="create-incubation-screen">
      <button className="back-button-screen" onClick={handleBackClick}>
        <i className="fas fa-arrow-left"></i>
        <span>Back</span>
      </button>
      <div className="add-organization-container">
        <Card style={{ width: '500px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
          <h2 className="chart-title">{getTitle()}</h2>
          
          <div className="form-group">
            <label className="form-label">Organization Name</label>
            <Input
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              type="text"
              placeholder="Enter organization name"
              className='add-organization-form-input'
            />
          </div>

          <div className="form-group">
            <label className="form-label">Organization Type</label>
            <Select
              options={organizationTypeOptions}
              value={organizationType}
              onChange={(e) => {
                const newValue = e.target?.value || e.value || e;
                console.log('Select onChange - new value:', newValue);
                console.log('Current organizationType state:', organizationType);
                console.log('Available options:', organizationTypeOptions);
                setOrganizationType(newValue);
                console.log('Set organizationType to:', newValue);
              }}
              placeholder="Select organization type"
              className='add-organization-form-input'
            />
          </div>

          <div className="form-actions">
            <Button
              variant="outline"
              size="medium"
              className="action-button"
              onClick={handleBackClick}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              className="action-button"
              disabled={submitting}
              ripple
            >
              {submitting 
                ? (finalMode === 'edit' ? 'Updating...' : 'Adding...') 
                : (finalMode === 'edit' ? 'Update Organization' : 'Add Organization')
              }
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddOrganizationScreen;
